<script lang="ts">
  import { untrack } from 'svelte';
  import { api } from '../../lib/api/client';
  import type { CountNew, Recap, TimelineRecords, TopPlace } from '../../lib/api/types';
  import { speed } from '../../lib/utils/activities';
  import { count, monthAfter, monthBefore, monthName } from '../../lib/utils/insights';
  import { t } from '../../lib/i18n/index.ts';
  import { categoryIcon, change, dayName, dayShort, distance, modeColors, modeIcon, modeLabel, placeName, shortDate, span } from './format';

  // Recap is a month's or a year's timeline compared with the period before
  // (spec 005 FR-046), stepping back a period at a time, and the
  // member's records over their whole timeline (005 FR-047). Each day, place,
  // and trip away in them opens where it is shown in full.
  let { start = '', onDay, onPlace, onTrip }: {
    // start opens it on a year ("2026") or a month ("2026-07").
    start?: string;
    onDay: (date: string, entry?: string) => void;
    onPlace: (p: TopPlace) => void;
    onTrip: (start: string) => void;
  } = $props();

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const thisYear = now.getFullYear();
  const initial = untrack(() => start);
  let view = $state<'month' | 'year' | 'records'>(initial.length === 4 ? 'year' : 'month');
  let month = $state(initial.length === 7 ? initial : thisMonth);
  let year = $state(initial.length === 4 ? Number(initial) : thisYear);
  let recap = $state<Recap | null>(null);
  let records = $state<TimelineRecords | null>(null);
  let error = $state('');

  const period = $derived(view === 'year' ? String(year) : month);

  $effect(() => {
    const v = view, p = period;
    error = '';
    if (v === 'records') {
      api.get<TimelineRecords>(`/me/timeline/records?tz=${encodeURIComponent(tz)}`)
        .then((got) => (records = got)).catch((e) => (error = e.message));
      return;
    }
    api.get<Recap>(`/me/timeline/recap?period=${p}&tz=${encodeURIComponent(tz)}`)
      .then((got) => { if (got.period === period) recap = got; })
      .catch((e) => { error = e.message; recap = null; });
  });

  function step(by: number) {
    if (view === 'year') year += by;
    else month = by < 0 ? monthBefore(month) : monthAfter(month);
  }

  const atLatest = $derived(view === 'year' ? year >= thisYear : month >= thisMonth);
  const periodName = (p: string) => (p.length === 4 ? p : monthName(p));
  const shown = $derived(recap && recap.period === period ? recap : null);
  // before is the period before, for comparing, or null when it has no data.
  const before = $derived(shown && !shown.previous.empty ? shown.previous : null);
  const travelled = $derived(shown?.modes.filter((m) => m.seconds > 0 || m.distance_m > 0) ?? []);

  function versus(now: number, then: number | undefined): string {
    if (!before || then == null) return '';
    const c = change(now, then);
    return c ? t('timelineChangeFrom', { change: c, period: periodName(before.period) }) : '';
  }

  const visited = $derived(shown ? ([
    [t('timelinePlaces'), 'places', shown.places],
    [t('timelineCities'), 'cities', shown.cities],
    [t('timelineRegions'), 'regions', shown.regions],
    [t('timelineCountries'), 'countries', shown.countries],
  ] as [string, 'places' | 'cities' | 'regions' | 'countries', CountNew][]) : []);

  // placeOnDay is a record's day, after its place when it has one.
  const placeOnDay = (name: string | undefined, date: string) => (name ? t('timelinePlaceOnDay', { place: name, day: dayShort(date) }) : dayShort(date));
</script>

<div class="flex-1 overflow-y-auto p-4 sm:p-6">
  <div class="max-w-3xl mx-auto space-y-4">
    <div class="flex flex-wrap items-center gap-3">
      <div class="flex gap-1" role="tablist" aria-label={t('timelineRecap')}>
        <button role="tab" aria-selected={view === 'month'} class="btn btn-sm {view === 'month' ? 'btn-primary' : 'btn-secondary'}" onclick={() => (view = 'month')}>{t('timelineMonth')}</button>
        <button role="tab" aria-selected={view === 'year'} class="btn btn-sm {view === 'year' ? 'btn-primary' : 'btn-secondary'}" onclick={() => (view = 'year')}>{t('timelineYear')}</button>
        <button role="tab" aria-selected={view === 'records'} class="btn btn-sm {view === 'records' ? 'btn-primary' : 'btn-secondary'}" onclick={() => (view = 'records')}>{t('timelineRecords')}</button>
      </div>
      {#if view !== 'records'}
        <div class="flex items-center gap-1">
          <button class="btn btn-secondary btn-sm" aria-label={t('timelinePeriodBefore', { period: view })} onclick={() => step(-1)}><i aria-hidden="true" class="las la-angle-left"></i></button>
          <span class="px-2 font-medium min-w-36 text-center" aria-live="polite">{periodName(period)}</span>
          <button class="btn btn-secondary btn-sm" aria-label={t('timelinePeriodAfter', { period: view })} disabled={atLatest} onclick={() => step(1)}><i aria-hidden="true" class="las la-angle-right"></i></button>
        </div>
      {/if}
    </div>

    {#if error}
      <p class="text-sm" style="color: var(--theme-error);">{error}</p>
    {:else if view === 'records'}
      {#if records}
        {@const r = records}
        {#if !r.farthest && !r.longest_trip_away && !r.longest_travel_day && !r.most_places_day && !r.fastest_trip}
          <p class="text-sm text-text-muted">{t('timelineNoRecords')}</p>
        {:else}
          <section class="card p-5">
            <h2 class="font-semibold mb-2">{t('timelineRecords')}</h2>
            <ul class="divide-y" style="border-color: var(--theme-surface1);">
              {#if r.farthest}
                {@const f = r.farthest}
                <li>
                  <button class="w-full text-left py-3 flex items-center gap-3 hover:opacity-80" onclick={() => onDay(f.date)}>
                    <i aria-hidden="true" class="las la-map-pin text-xl"></i>
                    <span class="flex-1 min-w-0">
                      <span class="block text-xs text-text-muted">{t('timelineFarthest')}</span>
                      <span class="block font-semibold">{distance(f.distance_m)}</span>
                      <span class="block text-xs text-text-muted truncate">{placeOnDay(f.name, f.date)}</span>
                    </span>
                    <i aria-hidden="true" class="las la-angle-right text-text-muted"></i>
                  </button>
                </li>
              {/if}
              {#if r.longest_trip_away}
                {@const away = r.longest_trip_away}
                <li>
                  <button class="w-full text-left py-3 flex items-center gap-3 hover:opacity-80" onclick={() => onTrip(away.start)}>
                    <i aria-hidden="true" class="las la-suitcase text-xl"></i>
                    <span class="flex-1 min-w-0">
                      <span class="block text-xs text-text-muted">{t('timelineLongestTripAway')}</span>
                      <span class="block font-semibold">{t('timelineNightCount', { n: away.nights })}</span>
                      <span class="block text-xs text-text-muted">{t('timelineTripAwayRecord', { start: shortDate(away.start), end: shortDate(away.end), cities: away.cities })}</span>
                    </span>
                    <i aria-hidden="true" class="las la-angle-right text-text-muted"></i>
                  </button>
                </li>
              {/if}
              {#if r.longest_travel_day}
                {@const d = r.longest_travel_day}
                <li>
                  <button class="w-full text-left py-3 flex items-center gap-3 hover:opacity-80" onclick={() => onDay(d.date)}>
                    <i aria-hidden="true" class="las la-route text-xl"></i>
                    <span class="flex-1 min-w-0">
                      <span class="block text-xs text-text-muted">{t('timelineLongestTravelDay')}</span>
                      <span class="block font-semibold">{distance(d.distance_m)}</span>
                      <span class="block text-xs text-text-muted">{dayShort(d.date)}</span>
                    </span>
                    <i aria-hidden="true" class="las la-angle-right text-text-muted"></i>
                  </button>
                </li>
              {/if}
              {#if r.most_places_day}
                {@const d = r.most_places_day}
                <li>
                  <button class="w-full text-left py-3 flex items-center gap-3 hover:opacity-80" onclick={() => onDay(d.date)}>
                    <i aria-hidden="true" class="las la-map-marker text-xl"></i>
                    <span class="flex-1 min-w-0">
                      <span class="block text-xs text-text-muted">{t('timelineMostPlacesDay')}</span>
                      <span class="block font-semibold">{t('timelinePlaceCount', { n: d.places })}</span>
                      <span class="block text-xs text-text-muted">{dayShort(d.date)}</span>
                    </span>
                    <i aria-hidden="true" class="las la-angle-right text-text-muted"></i>
                  </button>
                </li>
              {/if}
              {#if r.most_countries_year}
                {@const y = r.most_countries_year}
                <li>
                  <button class="w-full text-left py-3 flex items-center gap-3 hover:opacity-80" onclick={() => { year = y.year; view = 'year'; }}>
                    <i aria-hidden="true" class="las la-globe-americas text-xl"></i>
                    <span class="flex-1 min-w-0">
                      <span class="block text-xs text-text-muted">{t('timelineMostCountriesYear')}</span>
                      <span class="block font-semibold">{t('timelineCountryCount', { n: y.countries })}</span>
                      <span class="block text-xs text-text-muted">{y.year}</span>
                    </span>
                    <i aria-hidden="true" class="las la-angle-right text-text-muted"></i>
                  </button>
                </li>
              {/if}
              {#if r.fastest_trip}
                {@const fast = r.fastest_trip}
                <li>
                  <button class="w-full text-left py-3 flex items-center gap-3 hover:opacity-80" onclick={() => onDay(fast.date, fast.trip_id)}>
                    <i aria-hidden="true" class="las {modeIcon(fast.mode)} text-xl"></i>
                    <span class="flex-1 min-w-0">
                      <span class="block text-xs text-text-muted">{t('timelineFastestTrip')}</span>
                      <span class="block font-semibold">{speed(fast.top_speed_mps)}</span>
                      <span class="block text-xs text-text-muted">{t('timelineModeOnDay', { mode: modeLabel(fast.mode), day: dayShort(fast.date) })}</span>
                    </span>
                    <i aria-hidden="true" class="las la-angle-right text-text-muted"></i>
                  </button>
                </li>
              {/if}
              {#if r.days_since_trip_away != null}
                <li class="py-3 flex items-center gap-3">
                  <i aria-hidden="true" class="las la-home text-xl"></i>
                  <span class="flex-1 min-w-0">
                    <span class="block text-xs text-text-muted">{t('timelineSinceTripAway')}</span>
                    <span class="block font-semibold">{r.days_since_trip_away === 0 ? t('timelineAwayNow') : t('timelineDayCount', { n: r.days_since_trip_away })}</span>
                  </span>
                </li>
              {/if}
            </ul>
          </section>
        {/if}
      {/if}
    {:else if shown?.empty}
      <p class="card p-5 text-sm text-text-muted">{t('timelineNothingIn', { view, period: periodName(shown.period) })}</p>
    {:else if shown}
      <section class="card p-5 space-y-4">
        <h2 class="font-semibold">{t('timelineTravel')}</h2>
        <dl class="grid grid-cols-2 gap-3">
          <div>
            <dt class="text-xs text-text-muted">{t('timelineDistance')}</dt>
            <dd><span class="text-xl font-semibold tabular-nums">{distance(shown.distance_m)}</span>
              <span class="block text-xs text-text-muted">{versus(shown.distance_m, before?.distance_m)}</span></dd>
          </div>
          <div>
            <dt class="text-xs text-text-muted">{t('timelineTimeTraveling')}</dt>
            <dd><span class="text-xl font-semibold tabular-nums">{span(shown.travel_s)}</span>
              <span class="block text-xs text-text-muted">{versus(shown.travel_s, before?.travel_s)}</span></dd>
          </div>
        </dl>
        {#if travelled.length}
          <ul class="grid sm:grid-cols-2 gap-3">
            {#each travelled as m (m.mode)}
              {@const was = before?.modes.find((x) => x.mode === m.mode)}
              <li class="flex items-center gap-3">
                <i aria-hidden="true" class="las {modeIcon(m.mode)} text-2xl" style="color: {modeColors[m.mode]};"></i>
                <span>
                  <span class="block font-medium">{modeLabel(m.mode)}</span>
                  <span class="block text-sm text-text-muted">{was?.distance_m
                    ? t('timelineDistanceTimeChange', { distance: distance(m.distance_m), time: span(m.seconds), change: change(m.distance_m, was.distance_m) })
                    : t('timelineDistanceTime', { distance: distance(m.distance_m), time: span(m.seconds) })}</span>
                </span>
              </li>
            {/each}
          </ul>
        {/if}
      </section>

      <section class="card p-5 space-y-3">
        <h2 class="font-semibold">{t('timelineWhereYouWent')}</h2>
        <dl class="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {#each visited as [label, key, c] (key)}
            <div>
              <dt class="text-xs text-text-muted">{label}</dt>
              <dd><span class="text-xl font-semibold tabular-nums">{count(c.count)}</span>
                {#if c.new}<span class="text-sm" style="color: var(--theme-accent);"> {t('timelineNewCount', { n: c.new })}</span>{/if}
                <span class="block text-xs text-text-muted">{versus(c.count, before?.[key].count)}</span></dd>
            </div>
          {/each}
          <div>
            <dt class="text-xs text-text-muted">{t('timelineNightsAway')}</dt>
            <dd><span class="text-xl font-semibold tabular-nums">{count(shown.nights_away)}</span>
              <span class="block text-xs text-text-muted">{versus(shown.nights_away, before?.nights_away)}</span></dd>
          </div>
        </dl>
        <p class="text-xs text-text-muted">{t('timelineNewMeans', { period: periodName(shown.period) })}</p>
      </section>

      {#if shown.farthest || shown.busiest_day || shown.longest_travel_day}
        <section class="card p-5">
          <h2 class="font-semibold mb-1">{t('timelineHighlights')}</h2>
          <ul class="divide-y" style="border-color: var(--theme-surface1);">
            {#if shown.farthest}
              {@const f = shown.farthest}
              <li>
                <button class="w-full text-left py-3 flex items-center gap-3 hover:opacity-80" onclick={() => onDay(f.date)}>
                  <i aria-hidden="true" class="las la-map-pin text-xl"></i>
                  <span class="flex-1 min-w-0">
                    <span class="block text-xs text-text-muted">{t('timelineFarthest')}</span>
                    <span class="block font-semibold">{distance(f.distance_m)}</span>
                    <span class="block text-xs text-text-muted truncate">{placeOnDay(f.name, f.date)}</span>
                  </span>
                  <i aria-hidden="true" class="las la-angle-right text-text-muted"></i>
                </button>
              </li>
            {/if}
            {#if shown.busiest_day}
              {@const d = shown.busiest_day}
              <li>
                <button class="w-full text-left py-3 flex items-center gap-3 hover:opacity-80" onclick={() => onDay(d.date)}>
                  <i aria-hidden="true" class="las la-map-marker text-xl"></i>
                  <span class="flex-1 min-w-0">
                    <span class="block text-xs text-text-muted">{t('timelineBusiestDay')}</span>
                    <span class="block font-semibold">{t('timelinePlaceCount', { n: d.places })}</span>
                    <span class="block text-xs text-text-muted">{dayName(d.date)}</span>
                  </span>
                  <i aria-hidden="true" class="las la-angle-right text-text-muted"></i>
                </button>
              </li>
            {/if}
            {#if shown.longest_travel_day}
              {@const d = shown.longest_travel_day}
              <li>
                <button class="w-full text-left py-3 flex items-center gap-3 hover:opacity-80" onclick={() => onDay(d.date)}>
                  <i aria-hidden="true" class="las la-route text-xl"></i>
                  <span class="flex-1 min-w-0">
                    <span class="block text-xs text-text-muted">{t('timelineLongestTravelDay')}</span>
                    <span class="block font-semibold">{distance(d.distance_m)}</span>
                    <span class="block text-xs text-text-muted">{dayName(d.date)}</span>
                  </span>
                  <i aria-hidden="true" class="las la-angle-right text-text-muted"></i>
                </button>
              </li>
            {/if}
          </ul>
          {#if !shown.farthest}
            <p class="text-xs text-text-muted mt-2">{t('timelineFarthestNeedsHome')}</p>
          {/if}
        </section>
      {/if}

      {#if shown.top_places.length}
        <section class="card p-5">
          <h2 class="font-semibold mb-1">{t('timelineTopPlaces')}</h2>
          <ol>
            {#each shown.top_places as p, i (p.place_id ?? i)}
              <li>
                <button class="w-full text-left py-2 flex items-center gap-3 hover:opacity-80 disabled:hover:opacity-100" disabled={!p.place_id} onclick={() => onPlace(p)}>
                  <span class="w-5 text-sm text-text-muted tabular-nums">{i + 1}</span>
                  <i aria-hidden="true" class="las {categoryIcon(p.category)} text-xl"></i>
                  <span class="flex-1 min-w-0">
                    <span class="block font-medium truncate">{placeName(p)}</span>
                    {#if p.name && p.address}<span class="block text-xs text-text-muted truncate">{p.address}</span>{/if}
                  </span>
                  <span class="text-sm text-text-muted">{span(p.seconds)}</span>
                  {#if p.place_id}<i aria-hidden="true" class="las la-angle-right text-text-muted"></i>{/if}
                </button>
              </li>
            {/each}
          </ol>
        </section>
      {/if}
    {/if}
  </div>
</div>
